import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  Grid,
  Divider,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { 
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Flag as FlagIcon,
  FlagOutlined as FlagOutlinedIcon
} from "@material-ui/icons";
import ColorPicker from "../ColorPicker";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2)
  },
  colorSample: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: theme.spacing(1)
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  stagesList: {
    maxHeight: 300,
    overflow: "auto",
    marginTop: theme.spacing(2)
  },
  stageItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginBottom: theme.spacing(1)
  },
  addStageButton: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2)
  },
  formControl: {
    marginBottom: theme.spacing(2)
  },
  colorField: {
    display: "flex",
    alignItems: "center"
  },
  flagIcon: {
    marginRight: theme.spacing(1)
  },
  initialStage: {
    color: theme.palette.success.main
  },
  finalStage: {
    color: theme.palette.error.main
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4)
  }
}));

const COLORS = [
  "#9c27b0", // Purple
  "#2196f3", // Blue
  "#4caf50", // Green
  "#ff9800", // Orange
  "#f44336", // Red
  "#607d8b"  // Gray
];

const KanbanSettingsDialog = ({ open, onClose, queueId }) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [stages, setStages] = useState([]);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [stageForm, setStageForm] = useState({
    name: "",
    color: COLORS[0],
    isInitial: false,
    isFinal: false
  });

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data: settingsData } = await api.get("/kanban/settings", {
        params: { queueId }
      });
      
      // Find settings for this queue or create a default
      const queueSettings = settingsData.find(s => s.queueId === queueId);
      setSettings(queueSettings || { 
        queueId, 
        enabled: false, 
        autoCreateCards: false 
      });
      
      // Load stages for this queue
      if (queueId) {
        const { data: stagesData } = await api.get("/kanban/stages", {
          params: { queueId }
        });
        
        // Sort stages by position
        setStages(stagesData.sort((a, b) => a.position - b.position));
      }
      
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.settingsLoadError"));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && queueId) {
      loadSettings();
    }
  }, [open, queueId]);

  const handleSaveSettings = async () => {
    try {
      if (settings.id) {
        await api.put(`/kanban/settings/${settings.id}`, {
          enabled: settings.enabled,
          autoCreateCards: settings.autoCreateCards
        });
        
        toast.success(settings.enabled 
          ? i18n.t("kanban.notifications.enabled") 
          : i18n.t("kanban.notifications.disabled"));
      } else {
        await api.post("/kanban/settings", {
          queueId,
          enabled: settings.enabled,
          autoCreateCards: settings.autoCreateCards
        });
        
        toast.success(i18n.t("kanban.notifications.enabled"));
      }
      
      loadSettings();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.saveError"));
    }
  };

  const handleOpenStageDialog = (stage = null) => {
    if (stage) {
      setEditingStage(stage);
      setStageForm({
        name: stage.name,
        color: stage.color || COLORS[0],
        isInitial: stage.isInitial,
        isFinal: stage.isFinal
      });
    } else {
      setEditingStage(null);
      setStageForm({
        name: "",
        color: COLORS[0],
        isInitial: false,
        isFinal: false
      });
    }
    
    setStageDialogOpen(true);
  };

  const handleCloseStageDialog = () => {
    setStageDialogOpen(false);
    setEditingStage(null);
  };

  const handleStageFormChange = (field, value) => {
    setStageForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveStage = async () => {
    try {
      if (!stageForm.name) {
        toast.error(i18n.t("kanban.settings.stageName") + " " + i18n.t("backendErrors.ERR_REQUIRED"));
        return;
      }
      
      if (editingStage) {
        await api.put(`/kanban/stages/${editingStage.id}`, {
          name: stageForm.name,
          color: stageForm.color,
          isInitial: stageForm.isInitial,
          isFinal: stageForm.isFinal
        });
        
        toast.success(i18n.t("kanban.notifications.stageUpdated"));
      } else {
        await api.post("/kanban/stages", {
          name: stageForm.name,
          queueId,
          color: stageForm.color,
          isInitial: stageForm.isInitial,
          isFinal: stageForm.isFinal
        });
        
        toast.success(i18n.t("kanban.notifications.stageCreated"));
      }
      
      handleCloseStageDialog();
      loadSettings();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.stageSaveError"));
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (window.confirm(i18n.t("kanban.dialogs.deleteStage.message"))) {
      try {
        await api.delete(`/kanban/stages/${stageId}`);
        
        toast.success(i18n.t("kanban.notifications.stageDeleted"));
        loadSettings();
      } catch (err) {
        console.error(err);
        toast.error(i18n.t("kanban.notifications.stageDeleteError"));
      }
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{i18n.t("kanban.settings.title")}</DialogTitle>
        <DialogContent>
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            {i18n.t("kanban.buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{i18n.t("kanban.settings.title")}</DialogTitle>
        <DialogContent>
          <div className={classes.root}>
            <Typography variant="h6">{i18n.t("kanban.settings.general")}</Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.enabled || false}
                  onChange={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                  color="primary"
                />
              }
              label={i18n.t("kanban.settings.enableKanban")}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.autoCreateCards || false}
                  onChange={() => setSettings(prev => ({ ...prev, autoCreateCards: !prev.autoCreateCards }))}
                  color="primary"
                  disabled={!settings?.enabled}
                />
              }
              label={i18n.t("kanban.settings.autoCreateCards")}
            />
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveSettings}
              style={{ alignSelf: "flex-start", marginTop: 16 }}
            >
              {i18n.t("kanban.buttons.save")}
            </Button>
            
            <Divider className={classes.divider} />
            
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h6">{i18n.t("kanban.settings.stages")}</Typography>
              </Grid>
              <Grid item>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadSettings}
                  size="small"
                >
                  {i18n.t("refresh")}
                </Button>
              </Grid>
            </Grid>
            
            {stages.length === 0 ? (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                {i18n.t("kanban.settings.noStages")}
              </Typography>
            ) : (
              <List className={classes.stagesList}>
                {stages.map((stage) => (
                  <ListItem key={stage.id} className={classes.stageItem}>
                    <div className={classes.colorSample} style={{ backgroundColor: stage.color || "#9c27b0" }} />
                    <ListItemText 
                      primary={stage.name}
                      secondary={
                        <>
                          {stage.isInitial && (
                            <Tooltip title={i18n.t("kanban.settings.initialStage")}>
                              <FlagIcon fontSize="small" className={`${classes.flagIcon} ${classes.initialStage}`} />
                            </Tooltip>
                          )}
                          {stage.isFinal && (
                            <Tooltip title={i18n.t("kanban.settings.finalStage")}>
                              <FlagIcon fontSize="small" className={`${classes.flagIcon} ${classes.finalStage}`} />
                            </Tooltip>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleOpenStageDialog(stage)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleDeleteStage(stage.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
            
            <div className={classes.addStageButton}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenStageDialog()}
                disabled={!settings?.enabled}
              >
                {i18n.t("kanban.settings.addStage")}
              </Button>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            {i18n.t("kanban.buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Stage Edit Dialog */}
      <Dialog 
        open={stageDialogOpen} 
        onClose={handleCloseStageDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingStage 
            ? i18n.t("kanban.settings.editStage") 
            : i18n.t("kanban.settings.newStage")
          }
        </DialogTitle>
        <DialogContent>
          <TextField
            label={i18n.t("kanban.settings.stageName")}
            value={stageForm.name}
            onChange={(e) => handleStageFormChange("name", e.target.value)}
            fullWidth
            className={classes.formControl}
          />
          
          <div className={classes.colorField}>
            <Typography variant="body2" style={{ marginRight: 16 }}>
              {i18n.t("kanban.settings.stageColor")}:
            </Typography>
            <ColorPicker
              colors={COLORS}
              color={stageForm.color}
              onChange={(color) => handleStageFormChange("color", color)}
            />
          </div>
          
          <FormControlLabel
            control={
              <Switch
                checked={stageForm.isInitial}
                onChange={(e) => handleStageFormChange("isInitial", e.target.checked)}
                color="primary"
              />
            }
            label={i18n.t("kanban.settings.isInitialStage")}
            className={classes.formControl}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={stageForm.isFinal}
                onChange={(e) => handleStageFormChange("isFinal", e.target.checked)}
                color="primary"
              />
            }
            label={i18n.t("kanban.settings.isFinalStage")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStageDialog} color="secondary">
            {i18n.t("kanban.buttons.cancel")}
          </Button>
          <Button onClick={handleSaveStage} color="primary" variant="contained">
            {i18n.t("kanban.buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KanbanSettingsDialog;